import React, { useRef, useEffect, useState } from 'react';
import { Card, CardContent, Typography, Button, Tooltip,  IconButton, LinearProgress, TextField } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UploadFileIcon from '@mui/icons-material/UploadFile';

import { useWorkspaces } from 'contexts/WorkspacesContextProvider';
import { useService } from 'contexts/ServiceContextProvider';

import NewSecretModal from 'components/Modals/CreateNewSecret';
import DeleteSecretModal from '../components/Modals/DeleteSecret';
import DeleteStorageModal from '../components/Modals/DeleteStorage';
import DeleteResourceModal from '../components/Modals/DeleteResource';
import NewResourceModal from '../components/Modals/CreateNewResource';
import TextEditor from "../components/Custom/TextEditor";
import { useTranslation } from "react-i18next";
import '../css/views/ProjectDetails.css';
import '../css/main.css';
import { checkForWorkspaceStorageLimitError } from 'utils/WorkspaceStorageLimit';
import { useSnackbar } from 'contexts/SnackbarContextProvider';

import DownloadWarningModal from '../components/Modals/DownloadWarning'
import EncryptFileModal from '../components/Modals/EncryptFiles'

import LockIcon from '@mui/icons-material/Lock';
import EditIcon from '@mui/icons-material/Edit';
import LockOpenIcon from '@mui/icons-material/LockOpen';

import CustomFallback from "../components/Custom/CustomFallBack";
import EditSecretModal from 'components/Modals/EditSecretModal';

const ProjectDetails = () => {
    const { t } = useTranslation();

    const { showSnackbar } = useSnackbar();
    const { downloadEncryptedFile, getWorkspacePublicKey, UpdateSecret, UpdateResource, getProjectStatus, GetBoardDetails, UpdateImportantInformation, AddASecret, DeleteSecret, AddAResource, DeleteResource, AddFileToStorage, DeleteStorageFile, getProjectInformation } = useService();
    const [isLoading, setIsLoading] = useState(true);
    const [myBoardDetails, setMyBoardDetails] = useState({});
    const workspaceContext = useWorkspaces();
    const [avoidMisfire, setAvoidMisfire] = useState(false);
    const myWorkspaces = workspaceContext.workspaces;

    const [openSecretModal, setOpenSecretModal] = useState(false);
    const [openResourceModal, setOpenResourceModal] = useState(false);

    const [editedInformation, setEditedInformation] = useState('');
    const [, setOriginalInformation] = useState('');

    const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
    const [secretToDelete, setSecretToDelete] = useState(null);


    const [openStorageModal, setOpenStorageModal] = useState(false);
    const [storageFileToDelete, setStorageFileToDelete] = useState(null);

    const [openResourceDeleteModal, setOpenResourceDeleteModal] = useState(false);
    const [resourceToDelete, setResourceToDelete] = useState(null);
    const [, setStatus] = useState('');
    const [, setVisibility] = useState(true);
    const [projectID, setProjectID] = useState(true);

    const [secretVisibility, setSecretVisibility] = useState({});

    const [myUserPerms, setMyUserPerms] = useState({});

    const [uploadProgress, setUploadProgress] = useState({});


    const [openDownloadWarningModal, setOpenDownloadWarningModal] = useState(false);
    const [selectedFileUrl, setSelectedFileUrl] = useState(null);


    const [openEncryptFileModal, setOpenEncryptFileModal] = useState(false);

    const [encryptDecision, setEncryptDecision] = useState(false);
    const [publicKeyWorkspaceKey, setPublicKeyWorkspaceKey] = useState(null);
    const fileInputRef = useRef(null);
    const [, setProjectDetails] = useState(null);

    const handleEncryptFileModalClose = () => {
        setOpenEncryptFileModal(false);
    };

    const handleFileEncryption = async (shouldEncrypt) => {
        if (shouldEncrypt) {
            const response = await getWorkspacePublicKey(myWorkspaces[0].id);
            setPublicKeyWorkspaceKey(response);
        }

        setOpenEncryptFileModal(false);
        setEncryptDecision(shouldEncrypt);
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    async function generateAESKey() {
        return window.crypto.subtle.generateKey(
            {
                name: "AES-GCM",
                length: 256,
            },
            true,
            ["encrypt", "decrypt"]
        );
    }

    async function encryptFileWithAES(file, aesKey) {
        const fileArrayBuffer = await file.arrayBuffer();

        const iv = window.crypto.getRandomValues(new Uint8Array(12));
        const encryptedContent = await window.crypto.subtle.encrypt(
            {
                name: "AES-GCM",
                iv: iv,
            },
            aesKey,
            fileArrayBuffer
        );

        return { encryptedContent, iv };
    }

    async function encryptAESKeyWithRSA(aesKey, publicKeyPem) {
        const publicKey = await importRSAPublicKey(publicKeyPem);

        const aesKeyRaw = await window.crypto.subtle.exportKey("raw", aesKey);
        return window.crypto.subtle.encrypt(
            {
                name: "RSA-OAEP",
            },
            publicKey,
            aesKeyRaw
        );
    }

    async function importRSAPublicKey(pemKey) {
        const keyData = pemKey.replace(/-----BEGIN PUBLIC KEY-----/, '')
            .replace(/-----END PUBLIC KEY-----/, '')
            .replace(/\n/g, '');
        const binaryDerString = window.atob(keyData);
        const binaryDer = str2ab(binaryDerString);

        return window.crypto.subtle.importKey(
            'spki',
            binaryDer,
            {
                name: 'RSA-OAEP',
                hash: 'SHA-256',
            },
            true,
            ['encrypt']
        );
    }

    function str2ab(str) {
        const buf = new ArrayBuffer(str.length);
        const bufView = new Uint8Array(buf);
        for (let i = 0; i < str.length; i++) {
            bufView[i] = str.charCodeAt(i);
        }
        return buf;
    }

    async function hybridEncryptFile(file, publicKeyPem) {
        try {
            // Step 1: Generate an AES key
            const aesKey = await generateAESKey();

            // Step 2: Encrypt the file with AES
            const { encryptedContent, iv } = await encryptFileWithAES(file, aesKey);

            // Step 3: Encrypt the AES key with RSA
            const encryptedAESKey = await encryptAESKeyWithRSA(aesKey, publicKeyPem);

            // Step 4: Package the encrypted file content, encrypted AES key, and IV
            const encryptedBlob = new Blob([encryptedContent], { type: file.type });

            return {
                encryptedFile: new File([encryptedBlob], `${file.name}.encrypted`, { type: file.type }),
                encryptedAESKey,
                iv
            };
        } catch (error) {
            console.error("Error during hybrid encryption:", error);
            throw error;
        }
    }

    const handleFileClick = async (file) => {

        try {
            const response = await downloadEncryptedFile(myWorkspaces[0].id, file.id);
            const decryptedFileUrl = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = decryptedFileUrl;
            let extension = file.file_extension;
            if (!extension) {
                const splitted = file.file.split('.');
                extension = splitted[splitted.length - 1] || ''; 
            }

            const originalFilename = extension
                ? `${file.filename}.${extension}`
                : file.filename;

            link.download = originalFilename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

        } catch (error) {
            console.error('Error fetching and decrypting file:', error);
        }

    };

    const handleProceedDownload = () => {
        window.open(selectedFileUrl, '_blank');
        setOpenDownloadWarningModal(false);
    };

    // Function to close the download warning modal
    const handleCloseDownloadWarningModal = () => {
        setOpenDownloadWarningModal(false);
        setSelectedFileUrl(null);  // Clear the file URL
    };

    const getFileType = (file_extension, is_encrypted = false) => {
        if (is_encrypted) { return "Encrypted File" }
        const fileExtensionMapping = {
            'pdf': 'Document',
            'doc': 'Document',
            'docx': 'Document',
            'xls': 'Document',
            'xlsx': 'Document',
            'ppt': 'Document',
            'pptx': 'Document',
            'txt': 'Document',
            'jpg': 'Image',
            'jpeg': 'Image',
            'png': 'Image',
            '7z': 'Archive',
            'rar': 'Archive',
            'zip': 'Archive',
            'encrypted file': "Encrypted File",
        };
        return fileExtensionMapping[file_extension] || 'Other';
    };

    const handleAddToStorage = async (event) => {
        const files = Array.from(event.target.files);
        event.target.value = '';

        const MAX_SIZE = 5000 * 1024 * 1024;  // Maximum file size allowed (5GB)
        const progressMap = {};

        for (const file of files) {
            if (file.size <= MAX_SIZE) {
                progressMap[file.name] = 0;

                let fileToUpload = file;
                let encryptedAESKey = null;
                let iv = null;

                if (encryptDecision) {
                    try {
                        const { encryptedFile, encryptedAESKey: aesKey, iv: fileIv } = await hybridEncryptFile(file, publicKeyWorkspaceKey);
                        fileToUpload = encryptedFile;
                        encryptedAESKey = aesKey;
                        iv = fileIv;
                    } catch (error) {
                        console.error("Error encrypting file:", error);
                        showSnackbar(t("project_details.file_encryption_fail"), "error");
                        continue;
                    }
                }

                const formData = new FormData();
                formData.append('file', fileToUpload);
                formData.append('board', myBoardDetails.id);
                const fileType = file.name.split('.').pop();
                formData.append('file_type', fileType);
                formData.append('is_encrypted', encryptDecision);

                if (encryptedAESKey && iv) {
                    formData.append('encrypted_aes_key', new Blob([encryptedAESKey]));
                    formData.append('iv', btoa(String.fromCharCode(...iv)));
                }

                try {
                    const response = await AddFileToStorage(
                        myWorkspaces[0].id,
                        projectID,
                        myBoardDetails.id,
                        formData,
                        (progressEvent) => {
                            if (progressEvent.lengthComputable) {
                                const progress = Math.floor((progressEvent.loaded / progressEvent.total) * 100);
                                progressMap[file.name] = progress;
                                setUploadProgress({ ...progressMap });
                            }
                        }
                    );

                    // Handle successful upload
                    if (response.status === 201) {
                        const newFile = {
                            id: response.data[0].id,
                            file: response.data[0].file,
                            filename: response.data[0].file.split('/').pop(),
                            file_type: getFileType(response.data[0].file_extension),  // Determine file type
                            is_encrypted: encryptDecision,  // Add encryption flag
                            encrypted_aes_key: encryptedAESKey ? btoa(String.fromCharCode(...new Uint8Array(encryptedAESKey))) : null,  // Store AES key in Base64
                            iv: iv ? btoa(String.fromCharCode(...iv)) : null,  // Store IV in Base64
                        };

                        const updatedBoardDetails = { ...myBoardDetails };
                        updatedBoardDetails.storage.push(newFile);
                        setMyBoardDetails(updatedBoardDetails);

                        showSnackbar(t("project_details.file_uploaded_success"), "success");
                    }
                } catch (error) {
                    checkForWorkspaceStorageLimitError(error, t, showSnackbar);
                } finally {
                    progressMap[file.name] = 100;
                    setTimeout(() => {
                        setUploadProgress((prevProgress) => {
                            const newProgress = { ...prevProgress };
                            delete newProgress[file.name];
                            return newProgress;
                        });
                    }, 1000);
                }
            } else {
                showSnackbar(t("project_details.file_uploaded_exceed"), "error");
            }
        }
    };

    const handleOpenResourceDeleteModal = (resourceID) => {
        setResourceToDelete(resourceID);
        setOpenResourceDeleteModal(true);
    };

    const handleCloseResourceDeleteModal = () => {
        setResourceToDelete(null);
        setOpenResourceDeleteModal(false);
    };

    const handleOpenStorageModal = (fileID) => {
        setStorageFileToDelete(fileID);
        setOpenStorageModal(true);
    };

    const handleCloseStorageModal = () => {
        setStorageFileToDelete(null);
        setOpenStorageModal(false);
    };

    const handleOpenConfirmationModal = (secretID) => {
        setSecretToDelete(secretID);
        setOpenConfirmationModal(true);
    };

    const handleCloseConfirmationModal = () => {
        setSecretToDelete(null);
        setOpenConfirmationModal(false);
    };

    const handleSaveClick = async (json) => {
        myBoardDetails.important_information = json;
        try {
            await UpdateImportantInformation(myWorkspaces[0].id, projectID, myBoardDetails.id, json);
            myBoardDetails.important_information = json;
        }
        catch (error) {
            checkForWorkspaceStorageLimitError(error, t, showSnackbar);
        }
    };

    const handleResourceModalOpen = () => {
        setOpenResourceModal(true);
    };

    const handleResourceModalClose = () => {
        setOpenResourceModal(false);
    };

    const handleSecretModalOpen = () => {
        setOpenSecretModal(true);
    };

    const handleSecretModalClose = () => {
        setOpenSecretModal(false);
    };

    const handleCreateResource = async (title, hyperlink, visibility) => {
        setAvoidMisfire(true);
        try {
            const response = await AddAResource(myWorkspaces[0].id, projectID, myBoardDetails.id, title, hyperlink, visibility);
            if (response) {

                const newResource = response.data;
                const updatedResources = [...myBoardDetails.resources, newResource];
                setMyBoardDetails({ ...myBoardDetails, resources: updatedResources });
            }

            handleResourceModalClose();
        } catch (error) {
            console.error('Creating resource Failed:', error.message);
        } finally {
            setTimeout(() => {
                setAvoidMisfire(false);
            }, 1000); // Adjust the delay time as needed
        }
        handleResourceModalClose();
    };

    const handleDeleteResource = async () => {
        setAvoidMisfire(true);
        try {
            const response = DeleteResource(myWorkspaces[0].id, projectID, myBoardDetails.id, resourceToDelete);
            if (response) {
                const updatedResources = myBoardDetails.resources.filter(resource => resource.id !== resourceToDelete);
                setMyBoardDetails({ ...myBoardDetails, resources: updatedResources });
            } else {
                console.error("Failed to delete storage file.");
            }
        } catch (error) {
            console.error('Deleting resource Failed:', error.message);
        } finally {
            setTimeout(() => {
                setAvoidMisfire(false);
            }, 1000); // Adjust the delay time as needed
        }
        handleCloseResourceDeleteModal();
    };

    const handleCreateSecret = async (title, secret, visibility) => {
        setAvoidMisfire(true);
        try {
            const response = await AddASecret(myWorkspaces[0].id, projectID, myBoardDetails.id, title, secret, visibility);
            const newSecret = { "id": response.data.id, "title": title, "secret": secret, "type_of_visibility": visibility }
            const updatedSecrets = [...myBoardDetails.secret_section, newSecret];
            setMyBoardDetails({ ...myBoardDetails, secret_section: updatedSecrets });

        } catch (error) {
            console.error('Creating secret Failed:', error.message);
        } finally {
            setTimeout(() => {
                setAvoidMisfire(false);
            }, 1000); // Adjust the delay time as needed
        }
        handleSecretModalClose();
    };

    const handleDeleteSecret = async () => {
        setAvoidMisfire(true);
        try {
            const response = DeleteSecret(myWorkspaces[0].id, projectID, myBoardDetails.id, secretToDelete);
            if (response) {
                const updatedSecret = myBoardDetails.secret_section.filter(secret => secret.id !== secretToDelete);
                setMyBoardDetails({ ...myBoardDetails, secret_section: updatedSecret });
            } else {
                console.error("Failed to delete storage file.");
            }
        } catch (error) {
            console.error('Deleting secret Failed:', error.message);
        } finally {
            setTimeout(() => {
                setAvoidMisfire(false);
            }, 1000); // Adjust the delay time as needed
        }
        handleCloseConfirmationModal();
    };

    const handleDeleteStorage = async () => {
        setAvoidMisfire(true);
        try {
            const response = DeleteStorageFile(myWorkspaces[0].id, projectID, myBoardDetails.id, storageFileToDelete);
            if (response) {
                const updatedStorage = myBoardDetails.storage.filter(storage => storage.id !== storageFileToDelete);
                setMyBoardDetails({ ...myBoardDetails, storage: updatedStorage });
            } else {
                console.error("Failed to delete storage file.");
            }
        } catch (error) {
            console.error('Deleting file Failed:', error.message);
        } finally {
            setTimeout(() => {
                setAvoidMisfire(false);
            }, 1000); // Adjust the delay time as needed
        }
        handleCloseStorageModal();
    };

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        if (!workspaceContext.loading && workspaceContext.workspaceWorkers.length > 0) {
            const userPerms = workspaceContext.getLoggedUserPermissions();
            setMyUserPerms(userPerms);
        }
    }, [workspaceContext.loading, workspaceContext.workspaceWorkers]);

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        async function fetchData() {
            const currentPath = window.location.pathname;
            const pathParts = currentPath.split('/');
            const projectId = pathParts[pathParts.length - 2];
            setProjectID(projectId);

            const [boardDetail] = await GetBoardDetails(myWorkspaces[0]?.id, projectId);

            const boardName = boardDetail.name.replace(/^Board for\s*/i, '');

            document.title = boardName + " Details - Workflow PRO";
            setMyBoardDetails(boardDetail);
            setEditedInformation(boardDetail.important_information);
            setOriginalInformation(boardDetail.important_information);

            const projectStatus = await getProjectStatus(myWorkspaces[0]?.id, projectId);
            setStatus(projectStatus.data.status);
            setVisibility(projectStatus.data.visibility);
            setIsLoading(false);

            const project = await getProjectInformation(myWorkspaces[0]?.id, projectId);


            setProjectDetails(project);

        }
        if (myWorkspaces[0]) {
            fetchData();
        }
    }, [myWorkspaces]);

    const handleCancelEditResource = () => {
        setEditingResource(null);
    };

    const copyToClipboard = (secret) => {
        showSnackbar(t("project_details.copy_secret"), "success")
        navigator.clipboard.writeText(secret);
    };

    const toggleSecretVisibility = (id) => {
        setSecretVisibility(prevState => ({
            ...prevState,
            [id]: !prevState[id]
        }));
    };

    const handleDescription = async (json) => {
        try {
            setEditedInformation(json);
            handleSaveClick(json);
        } catch (error) {
            console.error("Error updating task description:", error);
        }
    };

    const [editingResource, setEditingResource] = useState(null);
    const [editedResourceTitle, setEditedResourceTitle] = useState('');
    const [editedResourceLink, setEditedResourceLink] = useState('');
    const [resourceVisibility, setResourceVisibility] = useState(false);

    const toggleVisibility = () => {
        setResourceVisibility((prevVisibility) => !prevVisibility);
    };

    const handleEditResource = (resource) => {
        setEditingResource(resource.id);
        setEditedResourceTitle(resource.title);
        setEditedResourceLink(resource.hyperlink);
        setResourceVisibility(resource.visibility);
    };

    const handleSaveEditedResource = async (resourceId) => {
        const resourceData = {
            title: editedResourceTitle,
            hyperlink: editedResourceLink,
            visibility: resourceVisibility,
        };

        try {
            const response = await UpdateResource(
                myWorkspaces[0].id,
                projectID,
                myBoardDetails.id,
                resourceId,
                resourceData
            );

            if (response.status === 200) {
                const updatedResources = myBoardDetails.resources.map(resource =>
                    resource.id === resourceId
                        ? { ...resource, title: editedResourceTitle, hyperlink: editedResourceLink, visibility: resourceVisibility }
                        : resource
                );
                setMyBoardDetails({ ...myBoardDetails, resources: updatedResources });

                setEditingResource(null);
                showSnackbar(t("project_details.resource_updated_success"), "success");
            }
        } catch (error) {
            showSnackbar(t("project_details.resource_update_fail"), "error");
        }
    };

    const handleEditSecret = (secret) => {
        setSecretToEdit(secret);
        setOpenEditModal(true);
    };

    const handleEditModalClose = () => {
        setOpenEditModal(false);
        setSecretToEdit(null);
    };

    const [openEditModal, setOpenEditModal] = useState(false);
    const [secretToEdit, setSecretToEdit] = useState(null);

    const handleUpdateSecret = async (id, updatedTitle, updatedSecret, updatedVisibility) => {
        const utf8ToB64 = (str) => {
            const utf8Encoder = new TextEncoder();
            const utf8Bytes = utf8Encoder.encode(str);
            return btoa(String.fromCharCode(...utf8Bytes));
        };
        const base64Secret = utf8ToB64(updatedSecret);

        const secretData = {
            id: id,
            title: updatedTitle,
            secret: base64Secret,
            type_of_visibility: updatedVisibility,
        };

        try {
            const response = await UpdateSecret(
                myWorkspaces[0].id,
                projectID,
                myBoardDetails.id,
                id,
                secretData
            );

            if (response.status === 200) {
                const updatedSecrets = myBoardDetails.secret_section.map(secret =>
                    secret.id === id
                        ? { ...secret, title: updatedTitle, secret: updatedSecret, type_of_visibility: updatedVisibility }
                        : secret
                );
                setMyBoardDetails({ ...myBoardDetails, secret_section: updatedSecrets });
                showSnackbar(t("project_details.secret_updated_success"), "success");
            }
        } catch (error) {
            showSnackbar(t("project_details.secret_update_fail"), "error");
        }
    };

    /* eslint-disable jsx-a11y/anchor-is-valid */
    return (
        <>
            {isLoading ? (
                <CustomFallback />
            ) : (
                <>
                    <div className="div-cards-details">
                        {/* Resource Card */}
                        <Card className="resource-card-details">
                            <CardContent>
                                <div className="div-resource-card-details">
                                    <Typography variant="h5" component="div" gutterBottom>
                                        {t('project_details.resources')}
                                    </Typography>
                                    <Button onClick={() => handleResourceModalOpen()} className="add-button-color-details">
                                        <span className="button-text-details"> {t('project_details.add_resource')} </span>  <AddIcon className="add-resource-icon-details" />
                                    </Button>
                                </div>
                                <Typography variant="body2" className="card-description-details">
                                    {t('project_details.resource_info')}
                                </Typography>
                                <div>
                                    <ul className="custom-ul-details">
                                        {myBoardDetails && myBoardDetails.resources &&
                                            myBoardDetails.resources.map((resource) => {
                                                if (resource.visibility || myUserPerms.is_owner || myUserPerms.is_admin) {
                                                    return (
                                                        <li className="custom-li-details" key={resource.id}>
                                                            {editingResource === resource.id ? (
                                                                <div className="editable-textfields-details">
                                                                    <div className="resource-textfields-details">
                                                                        {/* Editable fields using TextField */}
                                                                        <TextField
                                                                            label={t('project_details.resource_title')}
                                                                            value={editedResourceTitle}
                                                                            onChange={(e) => setEditedResourceTitle(e.target.value)}
                                                                            variant="outlined"
                                                                            size="small"
                                                                            className="edit-input-details"
                                                                        />
                                                                        <TextField
                                                                            label={t('project_details.resource_link')}
                                                                            value={editedResourceLink}
                                                                            onChange={(e) => setEditedResourceLink(e.target.value)}
                                                                            variant="outlined"
                                                                            size="small"
                                                                            className="edit-input-details"
                                                                        />
                                                                        <Tooltip title={resourceVisibility ? t('project_details.public') : t('project_details.private')}>
                                                                            <IconButton onClick={toggleVisibility} className="resource-lock-icon-details">
                                                                                {resourceVisibility ? <LockOpenIcon /> : <LockIcon />}
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    </div>
                                                                    <div className="resource-buttons-details">
                                                                        <Button className="accept-button" onClick={() => handleSaveEditedResource(resource.id)}>
                                                                            <CheckCircleIcon style={{ marginRight: 4 }} />
                                                                            {t('global.save')}
                                                                        </Button>
                                                                        <Button className="decline-button" onClick={handleCancelEditResource} sx={{ ml: 1 }}>
                                                                            <CancelIcon style={{ marginRight: 4 }} />
                                                                            {t('global.cancel')}
                                                                        </Button>
                                                                    </div>
                                                                </div>

                                                            ) : (
                                                                <div className="resource-textfields-noedit-details">
                                                                    <a href={resource.hyperlink} target="_blank" rel="noopener noreferrer" >
                                                                        {resource.title}
                                                                    </a>
                                                                    <div className="resource-buttons-details-non-edit">
                                                                        {!resource.visibility && (
                                                                            <Tooltip title={t("project_details.resource_lock")}>
                                                                                <LockIcon sx={{ color: "white", marginRight: "2px" }} />
                                                                            </Tooltip>
                                                                        )}
                                                                        <IconButton
                                                                            onClick={() => handleEditResource(resource)}
                                                                            disabled={!(myUserPerms?.is_admin || myUserPerms?.is_owner)}
                                                                            className="white-txt-color"
                                                                        >
                                                                            <EditIcon />
                                                                        </IconButton>
                                                                        <IconButton
                                                                            color="error"
                                                                            onClick={() => handleOpenResourceDeleteModal(resource.id)}
                                                                            disabled={!(myUserPerms?.is_admin || myUserPerms?.is_owner)}
                                                                        >
                                                                            <DeleteIcon />
                                                                        </IconButton>
                                                                    </div>
                                                                </div>
                                                            )}

                                                        </li>
                                                    );
                                                }
                                                return null;
                                            })
                                        }
                                    </ul>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Important Information Card */}
                        <Card className="resource-card-details">
                            <CardContent className="white-txt-color">
                                <Typography variant="h5" component="div" gutterBottom>
                                    {t('project_details.important_info')}
                                </Typography>
                                <div className="important-information-box-details">
                                    <TextEditor
                                        handleDescription={handleDescription}
                                        value={editedInformation}
                                        from_important_info={true}
                                    />
                                </div>
                            </CardContent>
                        </Card>


                        {/* Password/Secret Section Card */}
                        <Card className="resource-card-details">
                            <CardContent>
                                <div className="div-resource-card-details">
                                    <Typography variant="h5" component="div" gutterBottom>
                                        {t('project_details.password_sect')}
                                    </Typography>
                                    <Button onClick={() => handleSecretModalOpen()} className="add-button-color-details">
                                        <span className="button-text-details"> {t('project_details.add_secret')} </span> <AddIcon className="add-resource-icon-details" />
                                    </Button>
                                </div>
                                <Typography variant="body2" className="card-description-details">
                                    {t('project_details.secret_info')}
                                </Typography>
                                <div>
                                    <ul className="custom-ul-details">
                                        {myBoardDetails && myBoardDetails.secret_section &&
                                            myBoardDetails.secret_section.map((secret) => {
                                                const hasPermission =
                                                    myUserPerms?.is_owner ||
                                                    (myUserPerms?.is_admin && (secret.type_of_visibility === "admin" || secret.type_of_visibility === "user")) ||
                                                    (!myUserPerms?.is_admin && !myUserPerms?.is_owner && secret.type_of_visibility === "user");

                                                return (
                                                    <li key={secret.id}>
                                                        <div className="div-secret-section-details">
                                                            <span className={`copy-text-details ${hasPermission ? 'copy-text-pointer-details' : ''}`} onClick={hasPermission ? () => copyToClipboard(secret.secret) : null}>
                                                                {secret.title} : {hasPermission && secretVisibility[secret.id] ? <span className="see-secret-color-details">{secret.secret}</span> : "********"}
                                                            </span>
                                                            <div style={{ marginRight: 0, marginLeft: "auto", alignItems: "center" }}>
                                                                {hasPermission && (
                                                                    <IconButton className="toggle-secret-btn-details" onClick={() => toggleSecretVisibility(secret.id)}>
                                                                        {secretVisibility[secret.id] ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                                    </IconButton>
                                                                )}
                                                                <IconButton
                                                                    onClick={() => handleEditSecret(secret)}
                                                                    sx={{ color: 'white', marginLeft: "auto" }}
                                                                    disabled={!hasPermission}
                                                                >
                                                                    <EditIcon />
                                                                </IconButton>
                                                                <Tooltip title={!hasPermission ? t('project_details.secrets_permission') : ""}>
                                                                    <span>
                                                                        <IconButton
                                                                            className={`delete-btn-details ${hasPermission ? 'secrets-delete-btn-error-details' : 'secrets-delete-btn-disabled-details'}`}
                                                                            onClick={() => handleOpenConfirmationModal(secret.id)}
                                                                            disabled={!hasPermission}
                                                                        >
                                                                            <DeleteIcon />
                                                                        </IconButton>
                                                                    </span>
                                                                </Tooltip>

                                                            </div>
                                                        </div>
                                                    </li>
                                                );
                                            })}
                                    </ul>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Cloud Storage for Files Card */}
                        <Card className="resource-card-details">
                            <CardContent>
                                <div className="div-resource-card-details">
                                    <Typography variant="h5" component="div" gutterBottom>
                                        {t('project_details.cloud_storage')}
                                    </Typography>
                                    <Button
                                        variant="text"
                                        component="span"
                                        className="view-task-upload-button"
                                        onClick={() => setOpenEncryptFileModal(true)} // Open encryption modal first
                                    >
                                        <UploadFileIcon className="view-task-attachments-icon" />
                                        {t("project_details.add_file")}
                                    </Button>
                                    <input
                                        accept="*"
                                        id="upload-button-file"
                                        multiple
                                        type="file"
                                        ref={fileInputRef} // Attach the ref here
                                        className="no-display"
                                        onChange={handleAddToStorage} // Trigger file selection after encryption modal is handled
                                    />
                                </div>

                                <Typography variant="body2" className="card-description-details">
                                    {t('project_details.files_info')}
                                </Typography>
                                <div>
                                    {Object.keys(uploadProgress).length > 0 && (
                                        Object.keys(uploadProgress).map((fileName) => (
                                            <div key={fileName} style={{ marginTop: '10px' }}>
                                                <Typography variant="body2" color="white">
                                                    {`${fileName}: ${uploadProgress[fileName]}%`}
                                                </Typography>
                                                <LinearProgress variant="determinate" value={uploadProgress[fileName]} />
                                            </div>
                                        ))
                                    )}
                                </div>
                                <div>
                                    <ul>
                                        {myBoardDetails && myBoardDetails.storage &&
                                            (myBoardDetails.storage.map((file) => (

                                                <li key={file.id}>
                                                    <div className="div-cloud-storage-details">

                                                        {/* eslint-disable-next-line  */}
                                                        <Typography className="file-text-details">
                                                            <span className="file-name-details"> {file.filename}: </span>
                                                            <a className="file-link-details" onClick={() => handleFileClick(file)}>
                                                                {getFileType(file.file_extension, file.is_encrypted)}
                                                            </a>
                                                        </Typography>
                                                        <Tooltip
                                                            title={!(myUserPerms?.is_admin || myUserPerms?.is_owner) ? t('project_details.files_permission') : ""}
                                                        >
                                                            <span>
                                                                <IconButton
                                                                    onClick={() => handleOpenStorageModal(file.id)}
                                                                    disabled={!(myUserPerms?.is_admin || myUserPerms?.is_owner)}
                                                                    className={`cloud-storage-icon-details ${(myUserPerms?.is_admin || myUserPerms?.is_owner) ? 'secrets-delete-btn-error-details' : 'secrets-delete-btn-disabled-details'}`}
                                                                >
                                                                    <DeleteIcon />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    </div>
                                                </li>
                                            )))}
                                    </ul>
                                </div>
                            </CardContent>
                        </Card>
                        <DeleteSecretModal openConfirmationModal={openConfirmationModal} handleCloseConfirmationModal={handleCloseConfirmationModal} handleDeleteSecret={handleDeleteSecret} avoidMisfire={avoidMisfire} />
                        <DeleteStorageModal openStorageModal={openStorageModal} handleCloseStorageModal={handleCloseStorageModal} handleDeleteStorage={handleDeleteStorage} avoidMisfire={avoidMisfire} />
                        <DeleteResourceModal openResourceDeleteModal={openResourceDeleteModal} handleCloseResourceDeleteModal={handleCloseResourceDeleteModal} handleDeleteResource={handleDeleteResource} avoidMisfire={avoidMisfire} />
                        <NewSecretModal openSecretModal={openSecretModal} handleSecretModalClose={handleSecretModalClose} handleCreateSecret={handleCreateSecret} avoidMisfire={avoidMisfire} myUserPerms={myUserPerms} />
                        <NewResourceModal openResourceModal={openResourceModal} handleResourceModalClose={handleResourceModalClose} handleCreateResource={handleCreateResource} avoidMisfire={avoidMisfire} myUserPerms={myUserPerms} />
                        <DownloadWarningModal
                            openDownloadWarningModal={openDownloadWarningModal}
                            handleCloseDownloadWarningModal={handleCloseDownloadWarningModal}
                            handleProceedDownload={handleProceedDownload}
                            avoidMisfire={avoidMisfire}
                        />
                        <EncryptFileModal
                            openEncryptFileModal={openEncryptFileModal}
                            handleEncryptFileModalClose={handleEncryptFileModalClose}
                            handleFileEncryption={handleFileEncryption}
                            avoidMisfire={false}
                        />
                        {secretToEdit && (
                            <EditSecretModal
                                openEditModal={openEditModal}
                                handleEditModalClose={handleEditModalClose}
                                handleUpdateSecret={handleUpdateSecret}
                                secretData={secretToEdit}
                                myUserPerms={myUserPerms}
                            />
                        )}
                    </div>
                </>
            )}
        </>
    );
};

export default ProjectDetails;
